import * as FEATURES from '../../layout/features';
import { Queue, createLoader } from '../loaders';
import AudioChannel from './AudioChannel';

/**
 * @class
 * @description Коллекция звуковых каналов
 * @memberof module:media.module:audio
 */
class ChannelsBundle {

    constructor(context, destination = null, channelsCount = 1) {

        this.context = context;
        this.destination = destination || (context && context.destination);
        this.gainNode = null;
        if (this.destination) {
            this.gainNode = context.createGainNode ? context.createGainNode() : context.createGain();
            this.gainNode.connect(this.destination);
        }

        this.channels = [];
        this.channelsCount = channelsCount;
    }

    destructor() {
        this.channelsCount = 0;
        this.channels = null;
        this.context = null;
        this.destination = null;
        this.gainNode = null;
    }

    get channelsCount() {
        return this.channels.length;
    }

    set channelsCount(val) {
        val = ~~val;
        let len = this.channels.length;
        while (len > val) {
            let ch = this.channels.pop();
            ch.destructor();
            len--;
        }
        while (len < val) {
            len = this.addChannel();
            len++;
        }
    }

    addChannel() {
        let channel = AudioChannel.create(this.context, this.gainNode);
        return this.channels.push(channel);
    }

    hasChannel(id) {
        if (id !== null && !isNaN(id)) {
            return (id >= 0 && id < this.channels.length);
        }
        return false;
    }

    getChannel(id) {
        if (this.hasChannel(id)) {
            return this.channels[id];
        }
    }

    get volume() {
        return this.gainNode ? this.gainNode.gain.value : 1;
    }

    /**
     * @writeonly
     */
    set volume(val) {
        val = val && !isNaN(val) ? Math.max(0, Math.min(1, val)) : 0;
        if (this.gainNode) {
            this.gainNode.gain.value = val;
        }
        else for (let ch of this.channels) {
            ch.volume = val;
        }
    }

    /**
     * Проигрывание звука
     * @param {String} file название файла без расширения (!)
     * @param {Boolean} loop нужно ни зацикливать звук
     * @param {Boolean} soft "щадящее" проигрывание: false: звук будет проигран в любом случае, даже если при этом займет "чужой" канал. true - звук будет проигран только в случае наличия свободного канала.
     * @param {Number} [channelID] номер канала, на котором нужно проиграть звук
     * @returns {AudioPlayer} канал
     */
    play(file, loop, soft = false, channelID = null) {
        let channel = (channelID === null)
            ? this.getFreeChannel(soft)
            : this.getChannel(channelID);

        if (channel) {
            channel.stop();
            channel.play(file, loop);
        }
        return channel;
    }

    /**
     * Остановка проигрывания звука на указанном канале
     * @param {Number} channelID номер канала
     */
    stop(channelID = null) {
        if (channelID === null) {
            for (let ch of this.channels) ch.stop();
        }
        else if (this.hasChannel(channelID)) {
            this.getChannel(channelID).stop();
        }
    }

    pause(channelID = null) {
        if (channelID === null) {
            for (let ch of this.channels) {
                ch.pause();
            }
        }
        else if (this.hasChannel(channelID)) {
            this.getChannel(channelID).pause();
        }
    }

    resume(channelID = null) {
        if (channelID === null) {
            for (let ch of this.channels) ch.resume();
        }
        else if (this.hasChannel(channelID)) {
            this.getChannel(channelID).resume();
        }
    }

    setVolume(val, channelID = null) {
        if (channelID === null) {
            this.volume = val;
        }
        else if (this.hasChannel(channelID)) {
            this.getChannel(channelID).volume = val;
        }
    }

    /** @ignore */
    getFreeChannel(soft = false) {

        let channels = [];
        if (soft) {
            channels = this.channels.filter(ch => !(ch.locked || ch.busy));
        }
        else {
            channels = this.channels.filter(ch => !ch.locked);
            channels.sort((a, b) => {
                // float up not busy channels, saving their order
                if (!a.busy || !b.busy) {
                    return ~~a.busy - ~~b.busy;
                }
                // then float up channels playing longer
                let k1 = a.playbackStatus.timeLeft / a.playbackStatus.duration,
                    k2 = b.playbackStatus.timeLeft / b.playbackStatus.duration;
                return (k1 <= k2) ? -1 : 1;
            });
        }
        return channels.shift();
    }

}

const SINGLE_CHANNEL = Symbol('SINGLE_CHANNEL');
//const SINGLE_CHANNEL_MODE = (!FEATURES.WEBAUDIO_SUPPORT && navigator.userAgent.toLowerCase().indexOf("mac") != -1);
const SINGLE_CHANNEL_MODE = !FEATURES.WEBAUDIO_SUPPORT && FEATURES.MOBILE;

window.AudioContext = window.AudioContext || window.webkitAudioContext;

/**
 * @class
 * @description Класс звукового микшера
 * @memberof module:media.module:audio
 * @param {Object} config конфиг каналов
 */
class AudioMixer {

    static get MP3_SUPPORT() {
        return FEATURES.MP3_SUPPORT;
    }

    static get WEBAUDIO_SUPPORT() {
        return FEATURES.WEBAUDIO_SUPPORT;
    }

    constructor(config) {

        config = config || {};
        if (!config.channels) {
            config.channels = {
                "default": 16
            }
        }

        this.context = FEATURES.WEBAUDIO_SUPPORT && window.AudioContext && new window.AudioContext();
        this.destination = this.context && this.context.destination;
        this.gainNode = null;
        if (this.destination) {
            this.gainNode = this.context.createGainNode ? this.context.createGainNode() : this.context.createGain();
            this.gainNode.connect(this.destination);
        }

        this.channels = new Map();

        if (SINGLE_CHANNEL_MODE) {
            this.setChannel(SINGLE_CHANNEL, 1);
        }
        else {
            if (config.channels) {
                for (let name of Object.keys(config.channels)) {
                    this.setChannel(name, ~~config.channels[name]);
                }
            }
        }
    }

    destructor() {
        this.clearChannels();
        this.context = null;
    }

    /**
     *
     * @param assets ассеты
     * @param [path="assets/sounds"] путь к звуковым файлам
     * @returns {Queue}
     */
    createLoaderQueue(assets, path = "assets/sounds") {
        let queue = new Queue();
        for (let asset of assets) {

            let src = asset.src;
            if (Array.isArray(src)) {
                for (let i = 0; i < src.length; i++) {
                    src[i] = `${path}/${src[i]}`;
                }
            }
            else {
                src = `${path}/${src}`;
            }

            let loader = createLoader(src, this.context);
            loader.name = asset.name || null;
            queue.add(loader);
        }
        return queue;
    }

    /**
     * Режим работы с одним каналом
     * @returns {boolean}
     */
    get singleChannelMode() {
        return SINGLE_CHANNEL_MODE;
    }

    get volume() {
        return this.gainNode ? this.gainNode.gain.value : 1;
    }

    set volume(val) {
        val = val && !isNaN(val) ? Math.max(0, Math.min(1, val)) : 0;
        if (this.gainNode) {
            this.gainNode.gain.value = val;
        }
        else for (let [, ch] of this.channels) {
            ch.volume = val;
        }
    }

    setChannel(id, size = 1) {
        if (this.channels.has(id)) {
            this.channels.get(id).channelsCount = size;
        }
        else {
            this.channels.set(id, new ChannelsBundle(this.context, this.gainNode, size))
        }
    }

    hasChannel(id) {
        return this.channels.has(id);
    }

    getChannel(id) {
        if (SINGLE_CHANNEL_MODE) return this.channels.get(SINGLE_CHANNEL);

        return this.channels.get(id);
    }

    clearChannels() {
        for (let [, ch] of this.channels) {
            ch.destructor();
        }
        this.channels.clear();
    }

    /**
     * Проигрывание звука
     * @param file
     * @param loop
     * @param soft
     * @param channelID
     * @returns {AudioChannel}
     */
    play(file, loop, soft = false, channelID = null) {
        let channel = this.getChannel(channelID);
        if (channel) {
            channel.stop();
            channel.play(file, loop, soft);
        }
        return channel;
    }

    /**
     * Остановка проигрывания звука на указанном канале
     * @param {Number} channelID номер канала
     */
    stop(channelID = null) {
        if (channelID === null) {
            for (let [, ch] of this.channels) ch.stop();
        }
        else if (this.hasChannel(channelID)) {
            this.getChannel(channelID).stop();
        }
    }

    /**
     * Пауза
     * @param channelID
     */
    pause(channelID = null) {
        if (channelID === null) {
            for (let [, ch] of this.channels) {
                ch.pause();
            }
        }
        else if (this.hasChannel(channelID)) {
            this.getChannel(channelID).pause();
        }
    }

    /**
     * Возобновление
     * @param channelID
     */
    resume(channelID = null) {
        if (channelID === null) {
            for (let [, ch] of this.channels) ch.resume();
        }
        else if (this.hasChannel(channelID)) {
            this.getChannel(channelID).resume();
        }
    }

    /**
     * Установка громкости
     * @param val
     * @param channelID
     */
    setVolume(val, channelID = null) {
        if (channelID === null) {
            this.volume = val;
        }
        else if (this.hasChannel(channelID)) {
            this.getChannel(channelID).volume = val;
        }
    }

}

export default AudioMixer;
